import React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

// F1.1/APPLICATION MODE · Portal COREX = anfitrión del Workspace Oficial embebido a pantalla completa.
// ConstruLink (barra compacta) lo provee ApplicationModeLayout. Aquí solo va el Workspace de COREX,
// que es dueño íntegro de su interfaz (Cinta Azul), API, motor, runtime y datos.
export default function CorexPortal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  // Mandato v55.1 · Bóveda Privada: el aislamiento ya NO se hace encerrando (kiosco) al usuario en un
  // solo proyecto, sino filtrando la lista "Mis Proyectos" en el backend (solo proyectos propios) y
  // ocultando Inicio/Datos/Catálogos maestros en el bundle. Por eso montamos el iframe para cualquier
  // sesión válida: si llegó por deep-link (MI PRESUPUESTO) abre su proyecto; si llegó por "Abrir COREX"
  // aterriza en su Lista de Proyectos privada. El maestro nunca se expone (lista filtrada + nav restringida).
  React.useEffect(() => {
    if (user === null) return; // null = sesión cargando (AuthContext)
    setReady(true); setFailed(false);
  }, [user, isAdmin, attempt]);

  // Puente de retorno inteligente (v55.1): el botón "← Volver al Diseño" del Workspace (iframe) emite
  // un postMessage según la ubicación → Lista de Proyectos vuelve a Inicio Multifamiliar; dentro de un
  // proyecto vuelve a la Base de Datos Estructural. Navegación por SPA (sin recarga completa).
  React.useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data) return;
      if (data.type === "COREX_BACK_TO_DESIGN") navigate("/software/estructural");
      else if (data.type === "COREX_BACK_TO_INICIO") navigate("/software/vivienda-multifamiliar");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate]);

  // Rediseño 'Vida y Contraste' (solo visual): dota al botón "← Volver al Diseño" del Workspace
  // (iframe, mismo origen) del acabado Celeste Metálico premium, sin recompilar el bundle COREX.
  const styleBackButton = React.useCallback((e) => {
    try {
      const doc = e.target.contentDocument;
      if (!doc || doc.getElementById("cl-back-metallic")) return;
      const st = doc.createElement("style");
      st.id = "cl-back-metallic";
      st.textContent = `
        .cx-back-design{
          color:#fff!important;border:1px solid #0369A1!important;border-radius:.55rem!important;
          background:linear-gradient(to bottom,#38BDF8 0%,#0284C7 55%,#0369A1 100%)!important;
          box-shadow:inset 0 1px 1px rgba(255,255,255,.6),inset 0 -2px 4px rgba(0,0,0,.28),0 4px 12px rgba(2,132,199,.5)!important;
          font-weight:800!important;letter-spacing:.02em!important;
          transition:filter .3s ease-out,box-shadow .3s ease-out,transform .12s ease-out!important;
        }
        .cx-back-design:hover{filter:brightness(1.10)!important;box-shadow:inset 0 1px 1px rgba(255,255,255,.85),inset 0 -2px 4px rgba(0,0,0,.28),0 6px 18px rgba(2,132,199,.72)!important;}
        .cx-back-design:active{transform:scale(.985)!important;}
      `;
      doc.head.appendChild(st);
    } catch (err) { /* cross-origin/no-op */ }
  }, []);

  if (failed) {
    return (
      <div className="h-full w-full grid place-items-center bg-white px-6" data-testid="corex-portal-error">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <span className="text-base font-bold text-slate-800">No se pudo preparar tu Oráculo privado</span>
          <p className="text-sm text-slate-500">Por seguridad no abrimos el motor sin tu copia privada lista. Inténtalo de nuevo en unos segundos.</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setAttempt((a) => a + 1)} data-testid="corex-portal-retry"
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-bold text-white hover:bg-sky-700 transition-colors">Reintentar</button>
            <button onClick={() => navigate("/software/vivienda-multifamiliar")} data-testid="corex-portal-back"
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors">Volver al Diseño</button>
          </div>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="h-full w-full grid place-items-center bg-white" data-testid="corex-portal-loading">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" size={28} />
          <span className="text-sm font-semibold">Preparando tu Oráculo Santisteban privado…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white" data-testid="corex-portal">
      <iframe
        title="Workspace Oficial de COREX"
        src="/corex-workspace/index.html"
        data-testid="corex-workspace-frame"
        onLoad={styleBackButton}
        className="w-full h-full border-0 block"
      />
    </div>
  );
}
